import React, { useState } from 'react';
import { 
  Grid, 
  Box, 
  Typography, 
  Divider,
  Paper,
  Chip,
  IconButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import MuiDialog from '../../SystemDesign/MuiDialog';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { alpha } from '@mui/material/styles';

const SeeMoreSkill = ({ skill, triggerButtonComponent, isOpen, onClose }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  // Use internal state for open if not controlled externally
  const [internalOpen, setInternalOpen] = useState(false);
  
  // Handle dialog state - check if open state is controlled externally
  const open = isOpen !== undefined ? isOpen : internalOpen;
  
  const handleOpen = () => {
    if (isOpen === undefined) {
      setInternalOpen(true);
    }
  };
  
  const handleClose = () => {
    if (isOpen === undefined) {
      setInternalOpen(false);
    } else if (onClose) {
      onClose();
    }
  };

  if (!skill) return null;

  // Determine priority color
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return { bg: alpha(colors.redAccent[400], 0.2), text: colors.redAccent[400] };
      case "medium":
        return { bg: alpha(colors.Orange[400], 0.2), text: colors.Orange[400] };
      case "low":
        return { bg: alpha(colors.green[400], 0.2), text: colors.green[400] };
      default:
        return { bg: alpha(colors.grey[400], 0.2), text: colors.grey[400] };
    } 
  };

  // Function to render a section header
  const SectionHeader = ({ title }) => (
    <Typography
      variant="h6"
      sx={{
        color: colors.primary[400],
        fontWeight: 600,
        fontSize: '1.1rem',
        mt: 2,
        mb: 1
      }}
    >
      {title}
    </Typography>
  );

  // Function to render a field with its value
  const FieldDisplay = ({ label, value, gridProps = { xs: 12, md: 6 }, multiline = false }) => (
    <Grid item size={{...gridProps}}>
      <Typography variant="subtitle2" sx={{ color: colors.grey[800], fontWeight: 600, mb: 0.5 }}>
        {label}
      </Typography>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 1.5, 
          backgroundColor: colors.grey[70], 
          borderRadius: 1,
          minHeight: multiline ? '80px' : '40px',
          display: 'flex',
          alignItems: multiline ? 'flex-start' : 'center'
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            color: colors.grey[700],
            whiteSpace: multiline ? 'pre-wrap' : 'normal',
            overflowWrap: 'break-word'
          }}
        >
          {value || '—'}
        </Typography>
      </Paper>
    </Grid>
  );

  // Trigger button rules:
  // - Uncontrolled (isOpen undefined): render provided triggerButtonComponent or a default eye icon button
  // - Controlled (isOpen provided): do NOT render a default trigger; only render the provided triggerButtonComponent; otherwise render nothing
  const triggerButton = (() => {
    if (isOpen !== undefined) {
      return triggerButtonComponent || null;
    }
    return (
      triggerButtonComponent || (
        <IconButton 
          size="small"
          onClick={handleOpen}
          sx={{ 
            color: colors.grey[600],
            '&:hover': {
              color: colors.primary[400],
              backgroundColor: colors.primary[20]
            }
          }}
        >
          <RemoveRedEyeOutlinedIcon fontSize="small" />
        </IconButton>
      )
    );
  })();

  // Dialog content component
  const DialogContent = () => (
    <Box sx={{ py: 1 }}>
      {/* Basic Information Section */}
      <SectionHeader title="Basic Information" />
      <Grid container spacing={3}>
        <FieldDisplay label="Skill Code" value={skill.skill_code || skill.code} />
        <FieldDisplay label="Skill Name" value={skill.skill_name || skill.name} />
        
        <Grid item size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" sx={{ color: colors.grey[800], fontWeight: 600, mb: 0.5 }}>
            Priority
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip 
              label={skill.priority ? skill.priority.charAt(0).toUpperCase() + skill.priority.slice(1).toLowerCase() : 'Medium'} 
              sx={{ 
                backgroundColor: getPriorityColor(skill.priority).bg,
                color: getPriorityColor(skill.priority).text,
                fontWeight: 500,
                minWidth: '80px'
              }} 
            />
          </Box>
        </Grid>
        
        <FieldDisplay 
          label="Unit" 
          value={skill.unit?.specific_unit || (skill.unit?.name || '—')} 
        />
        
      </Grid>
      
      {/* Description - Full Width */}
      <Box sx={{ width: '100%', mt: 2 }}>
        <Typography variant="subtitle2" sx={{ color: colors.grey[800], fontWeight: 600, mb: 1 }}>
          Description
        </Typography>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 1.5, 
            backgroundColor: colors.grey[70], 
            borderRadius: 1,
            minHeight: '80px',
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%'
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              color: colors.grey[700],
              whiteSpace: 'pre-wrap',
              overflowWrap: 'break-word'
            }}
          >
            {skill.description || '—'}
          </Typography>
        </Paper>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      {/* Skill Levels Section */}
      <SectionHeader title="Skill Levels" />
      <Box sx={{ width: '100%', mt: 2 }}>
        {/* Niveau 1 - Awareness */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: colors.grey[800], fontWeight: 600, mb: 1 }}>
            Niveau 1 - Awareness
          </Typography>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 1.5, 
              backgroundColor: alpha(colors.skyBlue[400], 0.1), 
              borderRadius: 1,
              minHeight: '80px',
              display: 'flex',
              alignItems: 'flex-start',
              border: `1px solid ${alpha(colors.skyBlue[400], 0.2)}`,
              width: '100%'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: colors.grey[700],
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word'
              }}
            >
              {skill.awareness_description || '—'}
            </Typography>
          </Paper>
        </Box>
        
        {/* Niveau 2 - Skilled Application */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: colors.grey[800], fontWeight: 600, mb: 1 }}>
            Niveau 2 - Skilled Application
          </Typography>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 1.5, 
              backgroundColor: alpha(colors.Orange[400], 0.1), 
              borderRadius: 1,
              minHeight: '80px',
              display: 'flex',
              alignItems: 'flex-start',
              border: `1px solid ${alpha(colors.Orange[400], 0.2)}`,
              width: '100%'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: colors.grey[700],
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word'
              }}
            >
              {(skill.skilled_application_description || skill.application_description) || '—'}
            </Typography>
          </Paper>
        </Box>
        
        {/* Niveau 3 - Management */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: colors.grey[800], fontWeight: 600, mb: 1 }}>
            Niveau 3 - Management
          </Typography>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 1.5, 
              backgroundColor: alpha(colors.redAccent[400], 0.1), 
              borderRadius: 1,
              minHeight: '80px',
              display: 'flex',
              alignItems: 'flex-start',
              border: `1px solid ${alpha(colors.redAccent[400], 0.2)}`,
              width: '100%'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: colors.grey[700],
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word'
              }}
            >
              {skill.management_description || '—'}
            </Typography>
          </Paper>
        </Box>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      {/* References & Comments Section */}
      <SectionHeader title="References & Comments" />
      <Grid container spacing={4}>
        <FieldDisplay 
          label="External Reference" 
          value={skill.external_reference} 
          gridProps={{ xs: 12 }} 
          multiline={true} 
        />
        
        <FieldDisplay 
          label="Internal Reference" 
          value={skill.internal_reference} 
          gridProps={{ xs: 12 }} 
          multiline={true} 
        />
      </Grid>
      
      {/* Comments - Full Width */}
      <Box sx={{ width: '100%', mt: 2 }}>
        <Typography variant="subtitle2" sx={{ color: colors.grey[800], fontWeight: 600, mb: 1 }}>
          Comments
        </Typography>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 1.5, 
            backgroundColor: colors.grey[70], 
            borderRadius: 1,
            minHeight: '80px',
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%'
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              color: colors.grey[700],
              whiteSpace: 'pre-wrap',
              overflowWrap: 'break-word'
            }}
          >
            {skill.comments || '—'}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );

  return (
    <MuiDialog
      triggerButton={triggerButton}
      icon={<RemoveRedEyeOutlinedIcon />}
      title={`${skill.skill_code || skill.code} - ${skill.skill_name || skill.name}`}
      subtitle="Skill Details"
      content={<DialogContent />}
      open={open}
      onClose={handleClose}
      maxWidth="md"
    />
  );
};

export default SeeMoreSkill;